// Public blog post — /blog/:slug (+ /en, /es). Body is server-sanitized
// (nh3) so it's safe to render with dangerouslySetInnerHTML. Crawlers get
// per-post <head> from the FastAPI catchall; this Head covers client-side
// SPA navigation.

import { Link, useParams } from "react-router-dom";
import { Head } from "vite-react-ssg";

import { Section } from "@/components/marketing/Section";
import { SiteFooter } from "@/components/marketing/SiteFooter";
import { SiteNav } from "@/components/marketing/SiteNav";
import { useBlogPost } from "@/hooks/useBlog";
import { LOCALE_HREFLANG, SITE_ORIGIN, localePath } from "@/i18n/config";
import { useDict, useLocale } from "@/i18n/LocaleProvider";

function formatDate(iso: string | null | undefined, locale: string): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString(LOCALE_HREFLANG[locale as "pt" | "en" | "es"], {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

export function BlogPostPage() {
  const locale = useLocale();
  const t = useDict().blog;
  const { slug = "" } = useParams<{ slug: string }>();
  const { loading, post, notFound } = useBlogPost(slug);

  const blogBase = localePath(locale, "blog");
  const canonical = `${SITE_ORIGIN}${blogBase}/${slug}`;

  return (
    <div className="bg-bg min-h-screen text-content">
      <Head>
        <html lang={LOCALE_HREFLANG[locale]} />
        <title>{post ? `${post.title} — TrueHacking` : t.meta.title}</title>
        {post?.excerpt && <meta name="description" content={post.excerpt} />}
        <link rel="canonical" href={canonical} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={canonical} />
        <meta property="og:title" content={post ? post.title : t.meta.title} />
        {post?.excerpt && (
          <meta property="og:description" content={post.excerpt} />
        )}
        {post?.cover_image_url && (
          <meta property="og:image" content={post.cover_image_url} />
        )}
      </Head>

      <SiteNav page="blog" />

      <Section width="760px">
        <Link
          to={blogBase}
          className="inline-block font-mono text-[12px] text-muted hover:text-content no-underline mb-6"
        >
          {t.backToBlog}
        </Link>

        {loading ? (
          <div className="font-mono text-[12px] text-dim py-12">{t.loading}</div>
        ) : notFound || !post ? (
          <div className="font-mono text-[13px] text-muted py-12">
            {t.notFound}
          </div>
        ) : (
          <article
            className="rounded-lg overflow-hidden"
            style={{
              background: "rgb(var(--color-bg2))",
              border: "1px solid rgb(var(--color-stroke))",
            }}
          >
            {post.cover_image_url && (
              <img
                src={post.cover_image_url}
                alt=""
                className="w-full block max-h-[380px] object-cover"
              />
            )}
            <div className="p-6 sm:p-9">
              <div className="font-mono text-[11px] text-dim mb-3">
                {formatDate(post.published_at, locale)}
              </div>
              <h1 className="font-display font-bold text-content leading-[1.15] mb-6" style={{ fontSize: "clamp(28px, 4.5vw, 44px)" }}>
                {post.title}
              </h1>
              {/* Server-sanitized (nh3) — safe to render. */}
              <div
                className="blog-body"
                dangerouslySetInnerHTML={{ __html: post.body_html }}
              />
            </div>
          </article>
        )}
      </Section>

      <SiteFooter />
    </div>
  );
}
